import { useState, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';

import {
  generalInfoQuery,
  consolidatedScoresAggregateQuery,
  studentCountQuery
} from '../api';

import { navigation } from '../data';
import {
  popupMessagesDecisionInsightsOverall,
  popupMessagesDecisionInsightsNoGamesData,
  chartAttemptLabels,
  chartLines
} from './data';
import SubPagesLayout from '../../../UI/layout/subPages';
import PopupMessages from '../../../UI/introGameLayout/popupMessages';
import Scores from '../../../UI/game/scores';
import LineChart from '../../../UI/game/charts';

import './styles.css';

export default function InstructorInsightsOverall() {
  const navigate = useNavigate();

  const [filterBy, setFilterBy] = useState('Accuracy');
  const [barsData, setBarsData] = useState([]);
  const [chartData, setChartData] = useState([]);

  const [popupMessages, setPopupMessages] = useState([]);

  const filterByOptions = ['Accuracy', 'Points'];

  // Display-only student count (used in the popup message). The
  // /course-members/counts/ endpoint avoids paginating through the
  // full roster (which can be 800+ rows on large courses).
  const { data: studentCount } = useQuery(studentCountQuery()) || {};
  const { data: generalInfo } = useQuery(generalInfoQuery()) || {};
  // Aggregate variant of /api/scores/?completed=true. Backend returns:
  //   { summary: { <gameType>: { average_total_accuracy_session, session_count } },
  //     by_attempt: { <gameType>: [{ attempt, average_total_accuracy_session, session_count }] },
  //     unique_user_count }
  // Replaces the prior session-row payload + client-side flatten /
  // group-by-attempt math; everything we render on this page is now
  // pre-computed in SQL.
  const { data: aggregateScores } = useQuery(consolidatedScoresAggregateQuery({ completed: true }));

  const games = generalInfo?.games;
  const gamesSessions = generalInfo?.sessions;

  useEffect(() => {
    if(aggregateScores && games) {
      formatLeaderboardData();
      formatChartData();

      // Unique users that have completed at least one assignment —
      // backend hands this to us pre-computed.
      const uniqueUserCount = aggregateScores?.unique_user_count || 0;

      if(games.length > 0 && uniqueUserCount > 0) {
        const updatedMessages = popupMessagesDecisionInsightsOverall.map(message => ({
          ...message,
          text: message.text.replace('{allGamesUsersCount}', uniqueUserCount)
            .replace('{courseMembersCount}', studentCount || 0)
        }));
        setPopupMessages(updatedMessages);
      } else {
        setPopupMessages(popupMessagesDecisionInsightsNoGamesData);
      }
    }
  }, [aggregateScores, studentCount, filterBy, games]);

  const calculateYAxisConfig = (data) => {
    if (!data || data.length === 0) {
      return { domain: [0, 100], ticks: [0, 25, 50, 75, 100] };
    }

    const allValues = data.flatMap(d => [
      d.classification,
      d.normalBalance,
      d.balancedEquation,
      d.principles
    ]).filter(val => val !== null && val !== undefined);

    if (allValues.length === 0) {
      return { domain: [0, 100], ticks: [0, 25, 50, 75, 100] };
    }

    const minValue = Math.min(...allValues);

    const lowestPoint = Math.max(0, minValue - 10);

    const roundedLowest = Math.floor(lowestPoint / 5) * 5;

    const range = 100 - roundedLowest;
    const step = range / 4; // 4 steps = 5 points

    const ticks = [
      roundedLowest,
      Math.round(roundedLowest + step),
      Math.round(roundedLowest + step * 2),
      Math.round(roundedLowest + step * 3),
      100
    ];

    return {
      domain: [roundedLowest, 100],
      ticks
    };
  };

  // Bar chart data — backend's `summary` map is keyed by game_type
  // and already holds the class-average accuracy per type. Render a
  // bar only when session_count > 0 so empty game types don't
  // contribute zero-height bars (matches prior behavior, where a
  // missing per-type average was filtered out).
  //
  // Note: filterBy === 'Points' is still wired in state but the
  // aggregate response doesn't carry total_points. Until backend
  // exposes a points variant the page silently stays on Accuracy
  // (the UI exposes no toggle either, so this is unreachable today).
  const formatLeaderboardData = () => {
    const summary = aggregateScores?.summary || {};
    const toBar = (key, title, color) => {
      const entry = summary[key];
      if (!entry || !entry.session_count) return null;
      const score = (entry.average_total_accuracy_session ?? 0) * 100;
      if (!score) return null;
      return { title, score, color };
    };

    const sectionData = [
      toBar('classification',     'Classification',   '#ECF6F7'),
      toBar('normal_balance',     'Normal Balance',   '#FFF8EB'),
      toBar('balanced_equation',  'Double Entry',     '#EAE4F9'),
      toBar('principles',         'The Principles',   '#C7DBFF'),
    ].filter(Boolean);

    const barsData = sectionData.length > 0 ? [{
      title: 'Class Average Accuracy',
      bars: sectionData,
      barsLabel: '%',
    }] : [];

    setBarsData(barsData);
  }

  // Line chart data — read backend's `by_attempt` map per game type.
  // Each entry is { attempt, average_total_accuracy_session,
  // session_count }; we union the attempts present across all four
  // types (so a chart point appears whenever any type has data for
  // that attempt), restrict to attempts 1-3, and shape each row to
  // match the existing LineChart expectations.
  const formatChartData = () => {
    if (!aggregateScores) return;

    const byAttempt = aggregateScores?.by_attempt || {};
    const indexFor = (gameType) => {
      const entries = byAttempt[gameType] || [];
      return entries.reduce((acc, e) => {
        if (e?.attempt != null) acc[e.attempt] = e;
        return acc;
      }, {});
    };

    const classificationByAttempt = indexFor('classification');
    const normalByAttempt = indexFor('normal_balance');
    const balancedEquationByAttempt = indexFor('balanced_equation');
    const principlesByAttempt = indexFor('principles');

    const allAttempts = new Set([
      ...Object.keys(classificationByAttempt),
      ...Object.keys(normalByAttempt),
      ...Object.keys(balancedEquationByAttempt),
      ...Object.keys(principlesByAttempt),
    ]);

    const allowedAttempts = [1, 2, 3];

    const accuracyOf = (entry) =>
      entry?.average_total_accuracy_session != null
        ? entry.average_total_accuracy_session * 100
        : null;
    const countOf = (entry) =>
      entry?.session_count != null ? entry.session_count : null;

    const data = Array.from(allAttempts)
      .map(Number)
      .filter(attempt => allowedAttempts.includes(attempt))
      .sort((a, b) => a - b)
      .map(attempt => ({
        name: chartAttemptLabels[attempt] || `Attempt ${attempt}`,
        attempt,
        classification: accuracyOf(classificationByAttempt[attempt]),
        classificationCount: countOf(classificationByAttempt[attempt]),
        normalBalance: accuracyOf(normalByAttempt[attempt]),
        normalBalanceCount: countOf(normalByAttempt[attempt]),
        balancedEquation: accuracyOf(balancedEquationByAttempt[attempt]),
        balancedEquationCount: countOf(balancedEquationByAttempt[attempt]),
        principles: accuracyOf(principlesByAttempt[attempt]),
        principlesCount: countOf(principlesByAttempt[attempt]),
      }));

    setChartData(data);
  };

  const handlePrevButtonClick = () => {
    navigate('/instructor/insights/leaderboard/average-time');
  }

  const handleNextButtonClick = () => {
    let redirectUrl = '/instructor/insights/progress-scores/progress';
    if(games && gamesSessions && games.length > 0) {
      if(games.some(game => game.game_type === 'Account Classification' || game.game_type === 'Normal Balance')) {
        redirectUrl = '/instructor/insights/decision-insights/classifying-normal';
      } else if(games.some(game => game.game_type === 'Balanced Equation')) {
        redirectUrl = '/instructor/insights/decision-insights/double-entry';
      } else if(games.some(game => game.game_type === 'The Principles')) {
        redirectUrl = '/instructor/insights/decision-insights/principles';
      }
    }
    navigate(redirectUrl);
  }

  const handleFilterClick = (selectedFilterBy) => {
    setFilterBy(selectedFilterBy);
  }

  const maxScore = Math.max(...barsData.flatMap(section =>
    section.bars?.map(bar => bar.score) || []
  ));

  const hasChartData = chartData.some(d => {
    const hasAnyData =
      d.classification !== null ||
      d.normalBalance !== null ||
      d.balancedEquation !== null ||
      d.principles !== null;

    return hasAnyData && (d.attempt === 2 || d.attempt === 3);
  });

  const yAxisConfig = calculateYAxisConfig(chartData);

  const hasFinalData = chartData
    .filter(d => d.attempt === 3)
    .some(d =>
      d.classification !== null ||
      d.normalBalance !== null ||
      d.balancedEquation !== null ||
      d.principles !== null
    );

  const filteredChartData = hasFinalData
  ? chartData
  : chartData.filter(d => d.attempt !== 3);

  return (
    <SubPagesLayout
      navigation={navigation}
      onFirstButtonClick={handlePrevButtonClick}
      onSecondButtonClick={handleNextButtonClick}
      currentPage={1}
      pagesQuantity={5}
      contentClassName="decision-insights-content-container instructor-decision-insights-content-container"
    >
      <h1>Insights | Overall</h1>
      <div className="decision-insights-content">
        <PopupMessages
          messages={popupMessages}
          messageSize="xx-small"
          buttonText="Next"
          onButtonClick={handleNextButtonClick}
          additionalButtonText="Back"
          onAdditionalButtonClick={handlePrevButtonClick}
          isAvatarOnTop={true}
        />
        <Scores
          data={barsData}
          size="small"
          maxScore={maxScore}
          alignBarTitle="center"
        />

        {hasChartData && (
          <LineChart
            data={filteredChartData}
            lines={chartLines}
            title="Accuracy Score Across Attempts"
            xAxisKey="name"
            yAxisDomain={yAxisConfig.domain}
            yAxisTicks={yAxisConfig.ticks}
            yAxisFormatter={(value) => `${value}%`}
            tooltipFormatter={(value) => `${value.toFixed(1)}%`}
          />
        )}
      </div>
    </SubPagesLayout>
  );
}
